import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Filter,
  Download,
  MoreHorizontal,
  Phone,
  Mail,
  MessageSquare,
  Building2,
  Calendar,
  User,
  X,
  CheckSquare,
  Square,
  Loader2,
  Tag,
  ChevronDown,
  Flame,
  PhoneCall,
  PhoneOff,
  PhoneMissed,
  Clock,
  Ban,
  Send,
  MessageCircle,
  CalendarPlus,
  AlertCircle,
  Check,
  Eye,
  XCircle,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from '../../hooks/use-toast';
import ConfirmDialog from '../../components/ui/confirm-dialog';
import { useConfirm } from '../../components/ui/use-confirm';

import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { leadsAPI, usersAPI, tagsAPI, demosAPI } from '../../services/api';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';

// Country codes for phone input
const countryCodes = [
  { code: '+91', country: 'India', flag: '🇮🇳', regex: /^[6-9]\d{9}$/ },
  { code: '+1', country: 'USA', flag: '🇺🇸', regex: /^\d{10}$/ },
  { code: '+44', country: 'UK', flag: '🇬🇧', regex: /^\d{10}$/ },
  { code: '+971', country: 'UAE', flag: '🇦🇪', regex: /^\d{9}$/ },
  { code: '+65', country: 'Singapore', flag: '🇸🇬', regex: /^\d{8}$/ },
  { code: '+61', country: 'Australia', flag: '🇦🇺', regex: /^\d{9}$/ },
];

// Fallback data for static options
const leadStatuses = [
  { value: 'NEW', label: 'New' },
  { value: 'CONTACTED', label: 'Contacted' },
  { value: 'CALLBACK_SCHEDULED', label: 'Callback Scheduled' },
  { value: 'REATTEMPT_REQUIRED', label: 'Reattempt Required' },
  { value: 'ATTEMPTED_NO_ANSWER', label: 'Attempted - No Answer' },
  { value: 'NUMBER_SWITCHED_OFF', label: 'Number Switched Off' },
  { value: 'WRONG_NUMBER', label: 'Wrong Number' },
  { value: 'NOT_INTERESTED', label: 'Not Interested' },
  { value: 'CALL_LATER_TODAY', label: 'Call Later Today' },
  { value: 'CALL_LATER_THIS_WEEK', label: 'Call Later This Week' },
  { value: 'INTERESTED_NEEDS_DETAILS_FIRST', label: 'Interested - Needs Details' },
  { value: 'DEMO_SCHEDULED', label: 'Demo Scheduled' },
  { value: 'DEMO_QUALIFIED_SQD', label: 'Demo Qualified (SQD)' },
  { value: 'REJECTED_BY_EMPLOYEE', label: 'Rejected' }
];

const createdByLabels = {
  'SELF': 'Self',
  'ADMIN_ASSIGNED': 'Assigned',
  'WEBHOOK': 'WB'
};

// Get display label for lead source (combines created_by_type with source_channel)
const getSourceLabel = (lead) => {
  if (lead.created_by_type === 'WEBHOOK') {
    // Show WB + actual source channel
    const sourceLabel = sourceChannels.find(s => s.value === lead.source_channel)?.label || lead.source_channel;
    return `WB • ${sourceLabel}`;
  }
  if (lead.created_by_type === 'ADMIN_ASSIGNED') {
    return 'Assigned';
  }
  if (lead.created_by_type === 'SELF') {
    return 'Self';
  }
  // Fallback to source channel
  return sourceChannels.find(s => s.value === lead.source_channel)?.label || lead.source_channel || '-';
};

const industries = [
  'IT Services', 'Retail', 'Healthcare', 'Manufacturing', 'Banking & Finance', 
  'Education', 'Logistics', 'Pharmaceutical', 'Real Estate', 'Hospitality', 'Others'
];

const hrmsTools = ['None', 'Excel/Manual', 'GreytHR', 'Keka', 'Darwinbox', 'Zoho People', 'BambooHR', 'Tally', 'Other'];

// Source channels - uppercase values for backend enum
const sourceChannels = [
  { value: 'MANUAL_IMPORT', label: 'Manual Entry' },
  { value: 'DIRECT', label: 'Direct' },
  { value: 'GOOGLE_ADS', label: 'Google Ads' },
  { value: 'FACEBOOK_ADS', label: 'Facebook Ads' },
  { value: 'LINKEDIN', label: 'LinkedIn' },
  { value: 'LINKEDIN_ADS', label: 'LinkedIn Ads' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'WEBSITE', label: 'Website' },
  { value: 'REFERRAL', label: 'Referral' },
  { value: 'COLD_CALL', label: 'Cold Call' },
  { value: 'EVENT', label: 'Event' },
  { value: 'PARTNER', label: 'Partner' },
];

const getStatusColor = (status) => {
  const colors = {
    NEW: 'bg-blue-100 text-blue-700',
    CONTACTED: 'bg-teal-100 text-teal-700',
    CALLBACK_SCHEDULED: 'bg-indigo-100 text-indigo-700',
    REATTEMPT_REQUIRED: 'bg-pink-100 text-pink-700',
    DEMO_SCHEDULED: 'bg-purple-100 text-purple-700',
    DEMO_QUALIFIED_SQD: 'bg-emerald-100 text-emerald-700',
    CALL_LATER_TODAY: 'bg-amber-100 text-amber-700',
    CALL_LATER_THIS_WEEK: 'bg-orange-100 text-orange-700',
    INTERESTED_NEEDS_DETAILS_FIRST: 'bg-cyan-100 text-cyan-700',
    NOT_INTERESTED: 'bg-red-100 text-red-700',
    REJECTED_BY_EMPLOYEE: 'bg-red-100 text-red-700',
    ATTEMPTED_NO_ANSWER: 'bg-slate-100 text-slate-700',
  };
  return colors[status] || 'bg-slate-100 text-slate-700';
};

const getPriorityColor = (score) => {
  if (score >= 80) return 'bg-emerald-500';
  if (score >= 60) return 'bg-amber-500';
  if (score >= 40) return 'bg-orange-500';
  return 'bg-slate-400';
};

// Phone input component with country code
const PhoneInput = ({ value, onChange, countryCode, onCountryChange, error }) => {
  const selectedCountry = countryCodes.find(c => c.code === countryCode) || countryCodes[0];
  
  const validatePhone = (phone) => {
    if (!phone) return true;
    if (countryCode === '+91') {
      return /^[6-9]\d{9}$/.test(phone);
    }
    return phone.length >= 8;
  };
  
  const isValid = validatePhone(value);
  
  return (
    <div className="flex gap-2">
      <Select value={countryCode} onValueChange={onCountryChange}>
        <SelectTrigger className="w-[110px]">
          <SelectValue>
            <span className="flex items-center gap-1">
              <span>{selectedCountry.flag}</span>
              <span>{selectedCountry.code}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {countryCodes.map(c => (
            <SelectItem key={c.code} value={c.code}>
              <span className="flex items-center gap-2">
                <span>{c.flag}</span>
                <span>{c.code}</span>
                <span className="text-slate-400 text-xs">{c.country}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
        placeholder={countryCode === '+91' ? '98765 43210' : 'Phone number'}
        className={cn(
          'flex-1',
          !isValid && value && 'border-red-500 focus-visible:ring-red-500'
        )}
      />
    </div>
  );
};

const LeadsPage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [userTags, setUserTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [usePocForContact, setUsePocForContact] = useState(false);
  const { confirm, dialogProps } = useConfirm();

  const [showSQDOnly, setShowSQDOnly] = useState(false);
  
  // Call Outcome Modal State
  const [callOutcomeOpen, setCallOutcomeOpen] = useState(false);
  const [selectedLeadForCall, setSelectedLeadForCall] = useState(null);
  const [callOutcome, setCallOutcome] = useState({
    outcome: '',
    contact_method: 'PHONE_CALL',
    notes: '',
    details_communicated: false,
    whatsapp_sent: false,
    email_sent: false,
    call_before_demo_required: false,
    proposed_demo_date: '',
    proposed_demo_time: '',
    reattempt_method: '',
    callback_date: '',
    callback_time: ''
  });
  
  // Reject Lead Modal State
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedLeadForReject, setSelectedLeadForReject] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  
  // Schedule Demo Modal State
  const [scheduleDemoOpen, setScheduleDemoOpen] = useState(false);
  const [selectedLeadForDemo, setSelectedLeadForDemo] = useState(null);
  const [demoMissingFields, setDemoMissingFields] = useState([]);
  const [editingLeadData, setEditingLeadData] = useState({});
  const [demoForm, setDemoForm] = useState({
    demo_date: '',
    demo_time: '',
    duration: '60',
    notes: '',
    meeting_link: ''
  });
  
  const [newLead, setNewLead] = useState({
    lead_name: '',
    lead_company_name: '',
    real_company_name: '',
    director_name: '',
    poc_name: '',
    poc_mobile: '',
    poc_country_code: '+91',
    phone: '',
    phone_country_code: '+91',
    email: '',
    total_employee_count: '',
    industry: '',
    using_hrms_currently: 'no',
    current_hrms_tool: '',
    source_channel: 'manual_import',
    city: '',
    notes: '',
    tags: []
  });

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [leadsRes, usersRes, tagsRes] = await Promise.all([
          leadsAPI.list({ page: 1, page_size: 100 }),
          usersAPI.list(),
          tagsAPI.list()
        ]);
        const leadsData = leadsRes.data;
        setLeads(Array.isArray(leadsData) ? leadsData : (leadsData?.leads || []));
        setUsers(usersRes.data || []);
        const allTags = tagsRes.data || [];
        setTags(allTags);
        // Filter tags created by current user
        if (currentUser?.id) {
          setUserTags(allTags.filter(t => t.created_by === currentUser.id));
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentUser?.id]);

  // Handle "Use POC details for contact" checkbox
  useEffect(() => {
    if (usePocForContact) {
      setNewLead(prev => ({
        ...prev,
        lead_name: prev.poc_name,
        email: prev.email || '',
        phone: prev.poc_mobile,
        phone_country_code: prev.poc_country_code
      }));
    }
  }, [usePocForContact, newLead.poc_name, newLead.poc_mobile, newLead.poc_country_code]);

  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      const matchesSearch = 
        lead.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.contact_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.contact_phone?.includes(searchQuery);
      
      // Handle REJECTED filter separately
      let matchesStatus;
      if (statusFilter === 'REJECTED') {
        matchesStatus = lead.is_rejected === true;
      } else if (statusFilter === 'all') {
        matchesStatus = true;
      } else {
        matchesStatus = lead.current_status === statusFilter && !lead.is_rejected;
      }
      
      const matchesIndustry = industryFilter === 'all' || lead.industry === industryFilter;
      const matchesAssignee = assigneeFilter === 'all' || lead.assigned_to_user_id === assigneeFilter;
      
      // SQD filter - show only leads with DEMO_QUALIFIED_SQD status
      const matchesSQD = !showSQDOnly || lead.current_status === 'DEMO_QUALIFIED_SQD';

      return matchesSearch && matchesStatus && matchesIndustry && matchesAssignee && matchesSQD;
    });
  }, [leads, searchQuery, statusFilter, industryFilter, assigneeFilter, showSQDOnly]);
  
  // Count SQD leads for the badge
  const sqdCount = useMemo(() => {
    return leads.filter(lead => lead.current_status === 'DEMO_QUALIFIED_SQD').length;
  }, [leads]);

  const getAssigneeName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unassigned';
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) || '';
  };

  const toggleSelectAll = () => {
    if (selectedLeads.length === filteredLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(filteredLeads.map(l => l.id));
    }
  };

  const toggleSelectLead = (id) => {
    setSelectedLeads(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleExport = () => {
    const dataToExport = selectedLeads.length > 0 
      ? filteredLeads.filter(l => selectedLeads.includes(l.id))
      : filteredLeads;
    
    const csv = [
      ['Company', 'Contact', 'Phone', 'Email', 'Status', 'Industry', 'Assignee'].join(','),
      ...dataToExport.map(l => [
        l.company_name,
        l.contact_name,
        l.contact_phone,
        l.contact_email,
        l.current_status,
        l.industry,
        getAssigneeName(l.assigned_to_user_id)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads_export.csv';
    a.click();
  };

  const validateForm = () => {
    if (!newLead.real_company_name) return { valid: false, error: 'Real Company Name is required' };
    if (!newLead.director_name) return { valid: false, error: 'Director Name is required' };
    if (!newLead.poc_name) return { valid: false, error: 'POC Name is required' };
    if (!newLead.poc_mobile) return { valid: false, error: 'POC Mobile is required' };
    
    // Validate Indian phone number
    if (newLead.poc_country_code === '+91' && !/^[6-9]\d{9}$/.test(newLead.poc_mobile)) {
      return { valid: false, error: 'POC Mobile must be a valid Indian number (10 digits starting with 6-9)' };
    }
    
    return { valid: true };
  };

  const handleCreateLead = async () => {
    const validation = validateForm();
    if (!validation.valid) {
      toast({
        variant: 'destructive',
        title: 'Validation error',
        description: validation.error
      });
      return;
    }
    
    try {
      const fullPocMobile = `${newLead.poc_country_code}${newLead.poc_mobile}`;
      const leadData = {
        company_name: newLead.real_company_name,
        real_company_name: newLead.real_company_name,
        director_name: newLead.director_name,
        contact_name: newLead.poc_name,
        contact_email: newLead.email || null,
        contact_phone: fullPocMobile, // Use POC mobile as primary contact
        poc_name: newLead.poc_name,
        poc_mobile: fullPocMobile,
        total_employee_count: parseInt(newLead.total_employee_count) || null,
        industry: newLead.industry || null,
        using_hrms_currently: newLead.using_hrms_currently === 'yes',
        current_hrms_tool: newLead.using_hrms_currently === 'yes' ? newLead.current_hrms_tool : null,
        source_channel: newLead.source_channel || 'manual_import',
        city: newLead.city || null,
        notes: newLead.notes || null,
        tag_ids: newLead.tags.length > 0 ? newLead.tags : null
      };
      
      const response = await leadsAPI.create(leadData);
      setLeads(prev => [response.data, ...prev]);
      setIsCreateModalOpen(false);
      setUsePocForContact(false);
      setNewLead({
        lead_name: '',
        lead_company_name: '',
        real_company_name: '',
        director_name: '',
        poc_name: '',
        poc_mobile: '',
        poc_country_code: '+91',
        phone: '',
        phone_country_code: '+91',
        email: '',
        total_employee_count: '',
        industry: '',
        using_hrms_currently: 'no',
        current_hrms_tool: '',
        source_channel: 'MANUAL_IMPORT',
        city: '',
        notes: '',
        tags: []
      });
    } catch (error) {
      console.error('Failed to create lead:', error);
      const errorMsg = error.response?.data?.detail;
      if (Array.isArray(errorMsg)) {
        toast({
          variant: 'destructive',
          title: 'Validation error',
          description: errorMsg.map(e => e.msg).join(', ')
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Failed to create lead',
          description: errorMsg || error.message
        });
      }
    }
  };

  const handleUpdateLeadStatus = async (leadId, status) => {
    try {
      await leadsAPI.update(leadId, { current_status: status });
      setLeads(prev => prev.map(l => 
        l.id === leadId ? { ...l, current_status: status } : l
      ));
    } catch (error) {
      console.error('Failed to update lead:', error);
    }
  };


  const openCallOutcomeModal = (lead) => {
    setSelectedLeadForCall(lead);
    // Reset the form each time so mobile and desktop behave consistently
    setCallOutcome({
      outcome: '',
      contact_method: 'PHONE_CALL',
      notes: '',
      details_communicated: false,
      whatsapp_sent: false,
      email_sent: false,
      call_before_demo_required: false,
      proposed_demo_date: '',
      proposed_demo_time: '',
      reattempt_method: '',
      callback_date: '',
      callback_time: ''
    });
    setCallOutcomeOpen(true);
  };

  // Handle recording call outcome
  const handleRecordCallOutcome = async () => {
    if (!selectedLeadForCall || !callOutcome.outcome) return;
    
    try {
      const payload = {
        outcome: callOutcome.outcome,
        contact_method: callOutcome.contact_method,
        notes: callOutcome.notes || null
      };
      
      if (callOutcome.outcome === 'PICKED') {
        payload.details_communicated = callOutcome.details_communicated;
        payload.whatsapp_sent = callOutcome.whatsapp_sent;
        payload.email_sent = callOutcome.email_sent;
        payload.call_before_demo_required = callOutcome.call_before_demo_required;
        if (callOutcome.proposed_demo_date) {
          payload.proposed_demo_date = new Date(callOutcome.proposed_demo_date + 'T' + (callOutcome.proposed_demo_time || '10:00')).toISOString();
          payload.proposed_demo_time = callOutcome.proposed_demo_time;
        }
      } else if (callOutcome.outcome === 'DECLINED') {
        payload.reattempt_method = callOutcome.reattempt_method;
      } else if (callOutcome.outcome === 'CALLBACK_LATER') {
        payload.callback_date = new Date(callOutcome.callback_date + 'T' + (callOutcome.callback_time || '10:00')).toISOString();
        payload.callback_time = callOutcome.callback_time;
      }
      
      const response = await leadsAPI.recordCallOutcome(selectedLeadForCall.id, payload);
      
      // Update lead in local state
      setLeads(prev => prev.map(l => 
        l.id === selectedLeadForCall.id ? response.data : l
      ));
      
      setCallOutcomeOpen(false);
      setSelectedLeadForCall(null);
      setCallOutcome({
        outcome: '', contact_method: 'PHONE_CALL', notes: '',
        details_communicated: false, whatsapp_sent: false, email_sent: false,
        call_before_demo_required: false, proposed_demo_date: '', proposed_demo_time: '',
        reattempt_method: '', callback_date: '', callback_time: ''
      });
    } catch (error) {
      console.error('Failed to record call outcome:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to record call outcome',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  // Handle rejecting lead
  const handleRejectLead = async () => {
    if (!selectedLeadForReject || !rejectReason.trim()) return;
    
    try {
      await leadsAPI.reject(selectedLeadForReject.id, rejectReason);
      
      // Refresh leads list
      const response = await leadsAPI.list();
      const leadsData = response.data.leads || response.data || [];
      setLeads(leadsData);
      
      setRejectModalOpen(false);
      setSelectedLeadForReject(null);
      setRejectReason('');
    } catch (error) {
      console.error('Failed to reject lead:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to reject lead',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  // Handle opening Schedule Demo modal with validation
  const handleOpenScheduleDemo = (lead) => {
    const missing = [];
    if (!lead.contact_email) missing.push('Email');
    if (!lead.city) missing.push('City');
    if (!lead.total_employee_count) missing.push('Employee Count');
    if (!lead.industry) missing.push('Industry');
    if (lead.using_hrms_currently === null || lead.using_hrms_currently === undefined) missing.push('Using HRMS Currently');
    if (lead.using_hrms_currently && !lead.current_hrms_tool) missing.push('Current HRMS Tool');
    
    setSelectedLeadForDemo(lead);
    setEditingLeadData({
      contact_email: lead.contact_email || '',
      city: lead.city || '',
      total_employee_count: lead.total_employee_count || '',
      industry: lead.industry || '',
      using_hrms_currently: lead.using_hrms_currently,
      current_hrms_tool: lead.current_hrms_tool || ''
    });
    setDemoMissingFields(missing);
    setDemoForm({ demo_date: '', demo_time: '', duration: '60', notes: '', meeting_link: '' });
    setScheduleDemoOpen(true);
  };

  // Handle Schedule Demo submission
  const handleScheduleDemo = async () => {
    if (!selectedLeadForDemo || !demoForm.demo_date || !demoForm.demo_time) return;
    
    try {
      // If there were missing fields, save the lead data first
      if (demoMissingFields.length > 0) {
        await leadsAPI.update(selectedLeadForDemo.id, editingLeadData);
      }
      
      // Schedule the demo
      const demoDateTime = new Date(`${demoForm.demo_date}T${demoForm.demo_time}`);
      await demosAPI.create({
        lead_id: selectedLeadForDemo.id,
        demo_datetime: demoDateTime.toISOString(),
        duration_minutes: demoForm.duration,
        meeting_link: demoForm.meeting_link,
        remarks: demoForm.notes
      });
      
      // Refresh leads list
      const response = await leadsAPI.list();
      const leadsData = response.data.leads || response.data || [];
      setLeads(leadsData);
      
      setScheduleDemoOpen(false);
      setSelectedLeadForDemo(null);
      setDemoMissingFields([]);
      setEditingLeadData({});
      setDemoForm({ demo_date: '', demo_time: '', duration: '60', notes: '', meeting_link: '' });
    } catch (error) {
      console.error('Failed to schedule demo:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to schedule demo',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const toggleTagSelection = (tagId) => {
    setNewLead(prev => ({
      ...prev,
      tags: prev.tags.includes(tagId) 
        ? prev.tags.filter(t => t !== tagId)
        : [...prev.tags, tagId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading leads...</span>
      </div>
    );
  }

  // Calculate status counts
  const statusCounts = leads.reduce((acc, lead) => {
    acc[lead.current_status] = (acc[lead.current_status] || 0) + 1;
    return acc;
  }, {});
  
  // Status filter buttons configuration
  const statusFilterButtons = [
    { key: 'all', label: 'All', count: leads.length, color: 'bg-slate-100 text-slate-700' },
    { key: 'NEW', label: 'New', count: statusCounts['NEW'] || 0, color: 'bg-blue-100 text-blue-700' },
    { key: 'CONTACTED', label: 'Contacted', count: statusCounts['CONTACTED'] || 0, color: 'bg-teal-100 text-teal-700' },
    { key: 'CALLBACK_SCHEDULED', label: 'Callback', count: statusCounts['CALLBACK_SCHEDULED'] || 0, color: 'bg-indigo-100 text-indigo-700' },
    { key: 'REATTEMPT_REQUIRED', label: 'Reattempt', count: statusCounts['REATTEMPT_REQUIRED'] || 0, color: 'bg-pink-100 text-pink-700' },
    { key: 'ATTEMPTED_NO_ANSWER', label: 'No Answer', count: statusCounts['ATTEMPTED_NO_ANSWER'] || 0, color: 'bg-slate-100 text-slate-600' },
    { key: 'INTERESTED_NEEDS_DETAILS_FIRST', label: 'Needs Details', count: statusCounts['INTERESTED_NEEDS_DETAILS_FIRST'] || 0, color: 'bg-cyan-100 text-cyan-700' },
    { key: 'DEMO_SCHEDULED', label: 'Demo Scheduled', count: statusCounts['DEMO_SCHEDULED'] || 0, color: 'bg-purple-100 text-purple-700' },
    { key: 'NOT_INTERESTED', label: 'Not Interested', count: statusCounts['NOT_INTERESTED'] || 0, color: 'bg-red-100 text-red-700' },
    { key: 'REJECTED', label: 'Rejected', count: leads.filter(l => l.is_rejected).length, color: 'bg-red-100 text-red-700' },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100">Leads</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{filteredLeads.length} leads found {showSQDOnly && '(SQD Only)'}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* SQD Toggle Button */}
          <Button 
            variant={showSQDOnly ? "default" : "outline"} 
            size="sm"
            onClick={() => setShowSQDOnly(!showSQDOnly)}
            className={showSQDOnly ? "bg-orange-500 hover:bg-orange-600" : ""}
          >
            <Flame className={`w-4 h-4 sm:mr-2 ${showSQDOnly ? 'text-white' : 'text-orange-500'}`} />
            <span className="hidden sm:inline">SQD Leads</span>
            {sqdCount > 0 && (
              <Badge className="ml-1 sm:ml-2 bg-orange-100 text-orange-700" variant="secondary">
                {sqdCount}
              </Badge>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} className="hidden sm:flex">
            <Download className="w-4 h-4 mr-2" />
            Export {selectedLeads.length > 0 ? `(${selectedLeads.length})` : ''}
          </Button>
          <Button size="sm" className="bg-slate-800 hover:bg-slate-900" onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Add Lead</span>
          </Button>
        </div>
      </div>

      {/* Status Filter Buttons - Horizontal Scroll on Mobile */}
      <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide">
        <div className="flex gap-2 pb-2 min-w-max sm:flex-wrap sm:min-w-0">
          {statusFilterButtons.map(btn => (
            <Button
              key={btn.key}
              variant={statusFilter === btn.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(btn.key)}
              className={cn(
                'flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm',
                statusFilter === btn.key ? 'bg-slate-800' : ''
              )}
            >
              {btn.label}
              {btn.count > 0 && (
                <Badge className={cn('text-xs', statusFilter === btn.key ? 'bg-white text-slate-800' : btn.color)}>
                  {btn.count}
                </Badge>
              )}
            </Button>
          ))}
        </div>
      </div>

      {/* Filters - Mobile Responsive */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
        <div className="relative flex-1 min-w-0 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search leads..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white dark:bg-slate-800 w-full"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto sm:overflow-visible">
          <Select value={industryFilter} onValueChange={setIndustryFilter}>
            <SelectTrigger className="w-[140px] sm:w-[160px] bg-white dark:bg-slate-800 text-sm">
              <Building2 className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
              <SelectValue placeholder="Industry" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Industries</SelectItem>
              {industries.map(i => (
                <SelectItem key={i} value={i}>{i}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
            <SelectTrigger className="w-[140px] sm:w-[160px] bg-white dark:bg-slate-800 text-sm">
              <User className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
              <SelectValue placeholder="Assignee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assignees</SelectItem>
              {users.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="block lg:hidden space-y-3">
        {filteredLeads.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>No leads found</p>
          </div>
        ) : (
          filteredLeads.map((lead) => (
            <Card 
              key={lead.id} 
              className="bg-white dark:bg-slate-800 cursor-pointer active:bg-slate-50 dark:active:bg-slate-700"
              onClick={() => navigate(`/leads/${lead.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                      {lead.company_name}
                    </h3>
                    <p className="text-sm text-slate-500 truncate">
                      {lead.poc_name || lead.contact_name} • {lead.poc_mobile || lead.contact_phone}
                    </p>
                  </div>
                  <Badge className={cn('text-xs flex-shrink-0', lead.is_rejected ? 'bg-red-100 text-red-700' : getStatusColor(lead.current_status))}>
                    {lead.is_rejected ? 'Rejected' : formatStatus(lead.current_status)}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <Building2 className="w-3.5 h-3.5" />
                    <span className="truncate">{lead.industry || '—'}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <User className="w-3.5 h-3.5" />
                    <span className="truncate">{getAssigneeName(lead.assigned_to_user_id)}</span>
                  </div>
                  {lead.callback_date && !lead.callback_done && (
                    <div className="flex items-center gap-1.5 text-amber-600 col-span-2">
                      <Clock className="w-3.5 h-3.5" />
                      <span>Callback: {new Date(lead.callback_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
                  <Button 
                    size="sm" 
                    className="flex-1 h-9 bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={(e) => { e.stopPropagation(); openCallOutcomeModal(lead); }}
                  >
                    <Phone className="w-4 h-4 mr-1.5" />
                    Call
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button size="sm" variant="outline" className="h-9 w-9 p-0">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenScheduleDemo(lead); }}>
                        <CalendarPlus className="w-4 h-4 mr-2" />
                        Schedule Demo
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/leads/${lead.id}`); }}>
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={async (e) => { 
                          e.stopPropagation(); 
                          const ok = await confirm({
                            title: 'Reject this lead?',
                            description: 'This will mark the lead as rejected.',
                            confirmText: 'Reject',
                            cancelText: 'Cancel',
                            destructive: true
                          });
                          if (ok) {
                            leadsAPI.update(lead.id, { is_rejected: true }).then(() => {
                              setLeads(prev => prev.map(l => (l.id === lead.id ? { ...l, is_rejected: true } : l)));
                            });
                          }
                        }}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject Lead
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <Card className="hidden lg:block bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-900">
                <TableHead className="w-[50px]">
                  <Checkbox 
                    checked={selectedLeads.length === filteredLeads.length && filteredLeads.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead className="font-semibold">Lead</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Priority</TableHead>
                <TableHead className="font-semibold">Industry</TableHead>
                <TableHead className="font-semibold">Assigned To</TableHead>
                <TableHead className="font-semibold">Follow-up</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLeads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    No leads found
                  </TableCell>
                </TableRow>
              ) : filteredLeads.map((lead) => (
                <TableRow
                  key={lead.id}
                  className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox 
                      checked={selectedLeads.includes(lead.id)}
                      onCheckedChange={() => toggleSelectLead(lead.id)}
                    />
                  </TableCell>
                  <TableCell onClick={() => navigate(`/leads/${lead.id}`)}>
                    <div className="flex flex-col">
                      <span className="font-medium text-slate-800 dark:text-slate-100">{lead.company_name}</span>
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <span>{lead.contact_name}</span>
                        <span>•</span>
                        <span>{lead.poc_mobile || lead.contact_phone}</span>
                      </div>
                      {lead.tags && lead.tags.length > 0 && (
                        <div className="flex gap-1 mt-1">
                          {lead.tags.slice(0, 2).map((tag, idx) => (
                            <Badge key={typeof tag === 'object' ? tag.id : idx} variant="outline" className="text-xs py-0 px-1.5">
                              {typeof tag === 'object' ? tag.name : tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell onClick={() => navigate(`/leads/${lead.id}`)}>
                    <div className="flex flex-col gap-1">
                      <Badge className={cn('font-medium', lead.is_rejected ? 'bg-red-100 text-red-700' : getStatusColor(lead.current_status))}>
                        {lead.is_rejected ? 'Rejected' : formatStatus(lead.current_status)}
                      </Badge>
                      {/* Show last contacted if contacted */}
                      {['CONTACTED', 'CALLBACK_SCHEDULED', 'REATTEMPT_REQUIRED', 'DEMO_SCHEDULED'].includes(lead.current_status) && lead.updated_at && (
                        <span className="text-xs text-slate-400">
                          Last contacted: {new Date(lead.updated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </span>
                      )}
                      {lead.created_by_type && !['CONTACTED', 'CALLBACK_SCHEDULED', 'REATTEMPT_REQUIRED', 'DEMO_SCHEDULED'].includes(lead.current_status) && (
                        <span className="text-xs text-slate-400">
                          {getSourceLabel(lead)}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell onClick={() => navigate(`/leads/${lead.id}`)}>
                    <div className="flex items-center gap-2">
                      <div className={cn('w-2 h-2 rounded-full', getPriorityColor(lead.priority_score))} />
                      <span className="text-sm text-slate-600 dark:text-slate-400">{lead.priority_score}</span>
                    </div>
                  </TableCell>
                  <TableCell onClick={() => navigate(`/leads/${lead.id}`)}>
                    <span className="text-sm text-slate-600 dark:text-slate-400">{lead.industry || '-'}</span>
                  </TableCell>
                  <TableCell onClick={() => navigate(`/leads/${lead.id}`)}>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-slate-200 dark:bg-slate-600 flex items-center justify-center text-xs font-medium text-slate-600 dark:text-slate-300">
                        {getAssigneeName(lead.assigned_to_user_id).split(' ').map(n => n[0]).join('')}
                      </div>
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        {getAssigneeName(lead.assigned_to_user_id)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell onClick={() => navigate(`/leads/${lead.id}`)}>
                    {lead.callback_date && !lead.callback_done ? (
                      <div className="flex items-center gap-1 text-sm text-amber-600">
                        <Clock className="w-4 h-4" />
                        {new Date(lead.callback_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        {lead.callback_time && ` ${lead.callback_time}`}
                      </div>
                    ) : lead.next_follow_up_at ? (
                      <div className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                        <Calendar className="w-4 h-4" />
                        {new Date(lead.next_follow_up_at).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">—</span>
                    )}
                  </TableCell>
                  {/* Quick Actions Column */}
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-50"
                        title="Record Call"
                        onClick={() => { setSelectedLeadForCall(lead); setCallOutcomeOpen(true); }}
                      >
                        <PhoneCall className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0 text-purple-600 hover:bg-purple-50"
                        title="Schedule Demo"
                        onClick={() => handleOpenScheduleDemo(lead)}
                      >
                        <CalendarPlus className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                        title="Reject Lead"
                        onClick={() => { setSelectedLeadForReject(lead); setRejectModalOpen(true); }}
                      >
                        <Ban className="w-4 h-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/leads/${lead.id}`)}>
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(`tel:${lead.poc_mobile || lead.contact_phone}`)}>
                            <Phone className="w-4 h-4 mr-2" />
                            Call Now
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(`https://wa.me/${(lead.poc_mobile || lead.contact_phone)?.replace(/[^0-9]/g, '')}`)}>
                            <MessageSquare className="w-4 h-4 mr-2" />
                            WhatsApp
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(`mailto:${lead.contact_email}`)}>
                            <Mail className="w-4 h-4 mr-2" />
                            Email
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Lead Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Lead</DialogTitle>
            <DialogDescription>
              Add a new lead with complete details. Fields marked with * are mandatory.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Lead Details & Enrichment Section */}
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Lead Details & Enrichment</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="real_company_name">Real Company Name *</Label>
                  <Input
                    id="real_company_name"
                    value={newLead.real_company_name}
                    onChange={(e) => setNewLead({...newLead, real_company_name: e.target.value})}
                    placeholder="TechCorp Solutions Pvt Ltd"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="director_name">Director Name *</Label>
                  <Input
                    id="director_name"
                    value={newLead.director_name}
                    onChange={(e) => {
                      const directorName = e.target.value;
                      setNewLead(prev => ({
                        ...prev, 
                        director_name: directorName,
                        // Auto-fill POC name if empty or same as previous director name
                        poc_name: (!prev.poc_name || prev.poc_name === prev.director_name) ? directorName : prev.poc_name
                      }));
                    }}
                    placeholder="Enter director name"
                    className={!newLead.director_name && 'border-amber-300'}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="poc_name">POC Name * <span className="text-xs text-slate-400">(auto-filled from Director)</span></Label>
                  <Input
                    id="poc_name"
                    value={newLead.poc_name}
                    onChange={(e) => setNewLead({...newLead, poc_name: e.target.value})}
                    placeholder="Rahul Mehta"
                    className={!newLead.poc_name && 'border-amber-300'}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="poc_mobile">POC Mobile *</Label>
                  <PhoneInput
                    value={newLead.poc_mobile}
                    onChange={(v) => setNewLead({...newLead, poc_mobile: v})}
                    countryCode={newLead.poc_country_code}
                    onCountryChange={(c) => setNewLead({...newLead, poc_country_code: c})}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="total_employee_count">Total Employee Count</Label>
                  <Input
                    id="total_employee_count"
                    type="number"
                    value={newLead.total_employee_count}
                    onChange={(e) => setNewLead({...newLead, total_employee_count: e.target.value})}
                    placeholder="450"
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Industry</Label>
                  <Select 
                    value={newLead.industry} 
                    onValueChange={(v) => setNewLead({...newLead, industry: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      {industries.map(ind => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>Using HRMS Currently?</Label>
                  <Select 
                    value={newLead.using_hrms_currently} 
                    onValueChange={(v) => setNewLead({...newLead, using_hrms_currently: v, current_hrms_tool: v === 'no' ? '' : newLead.current_hrms_tool})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newLead.using_hrms_currently === 'yes' && (
                  <div className="grid gap-2">
                    <Label>Current HRMS Tool</Label>
                    <Select 
                      value={newLead.current_hrms_tool} 
                      onValueChange={(v) => setNewLead({...newLead, current_hrms_tool: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select tool" />
                      </SelectTrigger>
                      <SelectContent>
                        {hrmsTools.map(tool => (
                          <SelectItem key={tool} value={tool}>{tool}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">Contact Information (Optional)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newLead.email}
                    onChange={(e) => setNewLead({...newLead, email: e.target.value})}
                    placeholder="john@company.com"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={newLead.city}
                    onChange={(e) => setNewLead({...newLead, city: e.target.value})}
                    placeholder="Mumbai"
                  />
                </div>
              </div>
            </div>

            {/* Source & Tags */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Source Channel</Label>
                <Select 
                  value={newLead.source_channel} 
                  onValueChange={(v) => setNewLead({...newLead, source_channel: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceChannels.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Tags Dropdown */}
              {userTags.length > 0 && (
                <div className="grid gap-2">
                  <Label>Tags (Optional)</Label>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="justify-between">
                        <span className="flex items-center gap-2">
                          <Tag className="w-4 h-4" />
                          {newLead.tags.length > 0 ? `${newLead.tags.length} selected` : 'Select tags'}
                        </span>
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-[200px]">
                      {userTags.map(tag => (
                        <DropdownMenuCheckboxItem
                          key={tag.id}
                          checked={newLead.tags.includes(tag.id)}
                          onCheckedChange={() => toggleTagSelection(tag.id)}
                        >
                          <Badge className={`bg-${tag.color}-100 text-${tag.color}-700 text-xs`}>
                            {tag.name}
                          </Badge>
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={newLead.notes}
                onChange={(e) => setNewLead({...newLead, notes: e.target.value})}
                placeholder="Any additional notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateLead} 
              className="bg-slate-800 hover:bg-slate-900"
              disabled={!newLead.director_name || !newLead.poc_name || !newLead.poc_mobile}
            >
              Create Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Call Outcome Modal */}
      <Dialog open={callOutcomeOpen} onOpenChange={(open) => { 
        setCallOutcomeOpen(open); 
        if (!open) {
          setCallOutcome({
            outcome: '', contact_method: 'PHONE_CALL', notes: '',
            details_communicated: false, whatsapp_sent: false, email_sent: false,
            call_before_demo_required: false, proposed_demo_date: '', proposed_demo_time: '',
            reattempt_method: '', callback_date: '', callback_time: ''
          });
        }
      }}>
        <DialogContent className="left-0 top-auto bottom-0 translate-x-0 translate-y-0 w-full max-w-none rounded-t-xl p-4 sm:p-6 max-h-[85vh] overflow-y-auto sm:left-[50%] sm:top-[50%] sm:bottom-auto sm:translate-x-[-50%] sm:translate-y-[-50%] sm:max-w-[550px] sm:rounded-lg">
          <DialogHeader>
            <DialogTitle className="flex items-start gap-2 text-base sm:text-lg leading-snug break-words">
              <PhoneCall className="w-5 h-5" />
              Record Call Outcome - {selectedLeadForCall?.company_name}
            </DialogTitle>
            <DialogDescription>
              POC: {selectedLeadForCall?.poc_name} ({selectedLeadForCall?.poc_mobile})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Mobile: make the quick buttons easier to tap */}
            {/* Contact Method */}
            <div className="grid gap-2">
              <Label>Contact Method</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button 
                  variant={callOutcome.contact_method === 'PHONE_CALL' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setCallOutcome({...callOutcome, contact_method: 'PHONE_CALL'})}
                >
                  <Phone className="w-4 h-4 mr-2" /> Phone Call
                </Button>
                <Button 
                  variant={callOutcome.contact_method === 'WHATSAPP_VOICE' ? 'default' : 'outline'} 
                  size="sm"
                  onClick={() => setCallOutcome({...callOutcome, contact_method: 'WHATSAPP_VOICE'})}
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Voice
                </Button>
              </div>
            </div>

            {/* Call Outcome */}
            <div className="grid gap-2">
              <Label>What happened?</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button 
                  variant={callOutcome.outcome === 'PICKED' ? 'default' : 'outline'} 
                  className={callOutcome.outcome === 'PICKED' ? 'bg-emerald-600' : ''}
                  onClick={() => setCallOutcome({...callOutcome, outcome: 'PICKED'})}
                >
                  <PhoneCall className="w-4 h-4 mr-2" /> Picked
                </Button>
                <Button 
                  variant={callOutcome.outcome === 'DECLINED' ? 'default' : 'outline'}
                  className={callOutcome.outcome === 'DECLINED' ? 'bg-red-600' : ''}
                  onClick={() => setCallOutcome({...callOutcome, outcome: 'DECLINED'})}
                >
                  <PhoneOff className="w-4 h-4 mr-2" /> Declined
                </Button>
                <Button 
                  variant={callOutcome.outcome === 'CALLBACK_LATER' ? 'default' : 'outline'}
                  className={callOutcome.outcome === 'CALLBACK_LATER' ? 'bg-amber-600' : ''}
                  onClick={() => setCallOutcome({...callOutcome, outcome: 'CALLBACK_LATER'})}
                >
                  <Clock className="w-4 h-4 mr-2" /> Call Back Later
                </Button>
                <Button 
                  variant={callOutcome.outcome === 'NO_ANSWER' ? 'default' : 'outline'}
                  className={callOutcome.outcome === 'NO_ANSWER' ? 'bg-slate-600' : ''}
                  onClick={() => setCallOutcome({...callOutcome, outcome: 'NO_ANSWER'})}
                >
                  <PhoneMissed className="w-4 h-4 mr-2" /> No Answer
                </Button>
              </div>
            </div>

            {/* Conditional Fields based on outcome */}
            {callOutcome.outcome === 'PICKED' && (
              <div className="space-y-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <Label className="text-emerald-700 font-medium">Call Picked - Record Details</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="flex items-start gap-2 text-sm leading-snug">
                    <div className="pt-0.5">
                      <Checkbox checked={callOutcome.details_communicated} onCheckedChange={(c) => setCallOutcome({...callOutcome, details_communicated: c})} />
                    </div>
                    Details Communicated
                  </label>
                  <label className="flex items-start gap-2 text-sm leading-snug">
                    <div className="pt-0.5">
                      <Checkbox checked={callOutcome.whatsapp_sent} onCheckedChange={(c) => setCallOutcome({...callOutcome, whatsapp_sent: c})} />
                    </div>
                    WhatsApp Sent
                  </label>
                  <label className="flex items-start gap-2 text-sm leading-snug">
                    <div className="pt-0.5">
                      <Checkbox checked={callOutcome.email_sent} onCheckedChange={(c) => setCallOutcome({...callOutcome, email_sent: c})} />
                    </div>
                    Email Sent
                  </label>
                  <label className="flex items-start gap-2 text-sm leading-snug">
                    <div className="pt-0.5">
                      <Checkbox checked={callOutcome.call_before_demo_required} onCheckedChange={(c) => setCallOutcome({...callOutcome, call_before_demo_required: c})} />
                    </div>
                    Call Before Demo Required
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1">
                    <Label className="text-xs">Demo Date (if given)</Label>
                    <Input type="date" value={callOutcome.proposed_demo_date} onChange={(e) => setCallOutcome({...callOutcome, proposed_demo_date: e.target.value})} />
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-xs">Demo Time</Label>
                    <Input type="time" value={callOutcome.proposed_demo_time} onChange={(e) => setCallOutcome({...callOutcome, proposed_demo_time: e.target.value})} />
                  </div>
                </div>
                <div className="grid gap-1">
                  <Label className="text-xs">Discussion Notes</Label>
                  <Textarea value={callOutcome.notes} onChange={(e) => setCallOutcome({...callOutcome, notes: e.target.value})} placeholder="Key points discussed..." rows={2} />
                </div>
              </div>
            )}

            {callOutcome.outcome === 'DECLINED' && (
              <div className="space-y-3 p-4 bg-red-50 rounded-lg border border-red-200">
                <Label className="text-red-700 font-medium">Call Declined - How to Reattempt?</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Button variant={callOutcome.reattempt_method === 'whatsapp_voice' ? 'default' : 'outline'} size="sm" onClick={() => setCallOutcome({...callOutcome, reattempt_method: 'whatsapp_voice'})}>
                    WhatsApp Voice
                  </Button>
                  <Button variant={callOutcome.reattempt_method === 'email' ? 'default' : 'outline'} size="sm" onClick={() => setCallOutcome({...callOutcome, reattempt_method: 'email'})}>
                    Email
                  </Button>
                  <Button variant={callOutcome.reattempt_method === 'whatsapp_message' ? 'default' : 'outline'} size="sm" onClick={() => setCallOutcome({...callOutcome, reattempt_method: 'whatsapp_message'})}>
                    WhatsApp Message
                  </Button>
                </div>
                <Textarea value={callOutcome.notes} onChange={(e) => setCallOutcome({...callOutcome, notes: e.target.value})} placeholder="Additional notes..." rows={2} />
              </div>
            )}

            {callOutcome.outcome === 'CALLBACK_LATER' && (
              <div className="space-y-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <Label className="text-amber-700 font-medium">Schedule Callback</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1">
                    <Label className="text-xs">Callback Date *</Label>
                    <Input 
                      type="date" 
                      value={callOutcome.callback_date} 
                      onChange={(e) => setCallOutcome({...callOutcome, callback_date: e.target.value})} 
                      min={new Date().toISOString().split('T')[0]}
                      required 
                    />
                  </div>
                  <div className="grid gap-1">
                    <Label className="text-xs">Callback Time *</Label>
                    <Input type="time" value={callOutcome.callback_time} onChange={(e) => setCallOutcome({...callOutcome, callback_time: e.target.value})} required />
                  </div>
                </div>
                <Textarea value={callOutcome.notes} onChange={(e) => setCallOutcome({...callOutcome, notes: e.target.value})} placeholder="Callback notes..." rows={2} />
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button className="w-full sm:w-auto" variant="outline" onClick={() => setCallOutcomeOpen(false)}>Cancel</Button>
            <Button className="w-full sm:w-auto" 
              onClick={handleRecordCallOutcome}
              disabled={!callOutcome.outcome || (callOutcome.outcome === 'CALLBACK_LATER' && !callOutcome.callback_date)}
            >
              Save Outcome
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Demo Modal with Field Validation */}
      <Dialog open={scheduleDemoOpen} onOpenChange={(open) => {
        setScheduleDemoOpen(open);
        if (!open) {
          setSelectedLeadForDemo(null);
          setDemoMissingFields([]);
          setEditingLeadData({});
        }
      }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-start gap-2 text-base sm:text-lg leading-snug break-words">
              <CalendarPlus className="w-5 h-5 text-purple-600" />
              Schedule Demo - {selectedLeadForDemo?.company_name}
            </DialogTitle>
            <DialogDescription>
              POC: {selectedLeadForDemo?.poc_name || selectedLeadForDemo?.contact_name} ({selectedLeadForDemo?.poc_mobile || selectedLeadForDemo?.contact_phone})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Missing Fields Section */}
            {demoMissingFields.length > 0 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-4">
                <div className="flex items-center gap-2 text-amber-700 font-medium">
                  <AlertCircle className="w-5 h-5" />
                  Complete Required Information
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  {demoMissingFields.includes('Email') && (
                    <div className="space-y-1">
                      <Label className="text-amber-800 text-xs">Email *</Label>
                      <Input
                        type="email"
                        value={editingLeadData.contact_email || ''}
                        onChange={(e) => setEditingLeadData({...editingLeadData, contact_email: e.target.value})}
                        placeholder="email@company.com"
                        className="border-amber-300"
                      />
                    </div>
                  )}
                  {demoMissingFields.includes('City') && (
                    <div className="space-y-1">
                      <Label className="text-amber-800 text-xs">City *</Label>
                      <Input
                        value={editingLeadData.city || ''}
                        onChange={(e) => setEditingLeadData({...editingLeadData, city: e.target.value})}
                        placeholder="Mumbai"
                        className="border-amber-300"
                      />
                    </div>
                  )}
                  {demoMissingFields.includes('Employee Count') && (
                    <div className="space-y-1">
                      <Label className="text-amber-800 text-xs">Employee Count *</Label>
                      <Input
                        type="number"
                        value={editingLeadData.total_employee_count || ''}
                        onChange={(e) => setEditingLeadData({...editingLeadData, total_employee_count: parseInt(e.target.value) || 0})}
                        placeholder="100"
                        className="border-amber-300"
                      />
                    </div>
                  )}
                  {demoMissingFields.includes('Industry') && (
                    <div className="space-y-1">
                      <Label className="text-amber-800 text-xs">Industry *</Label>
                      <Select
                        value={editingLeadData.industry || ''}
                        onValueChange={(v) => setEditingLeadData({...editingLeadData, industry: v})}
                      >
                        <SelectTrigger className="border-amber-300">
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {industries.map(ind => (
                            <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {demoMissingFields.includes('Using HRMS Currently') && (
                    <div className="space-y-1">
                      <Label className="text-amber-800 text-xs">Using HRMS Currently? *</Label>
                      <Select
                        value={editingLeadData.using_hrms_currently === true ? 'yes' : editingLeadData.using_hrms_currently === false ? 'no' : ''}
                        onValueChange={(v) => setEditingLeadData({...editingLeadData, using_hrms_currently: v === 'yes'})}
                      >
                        <SelectTrigger className="border-amber-300">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {(demoMissingFields.includes('Current HRMS Tool') || editingLeadData.using_hrms_currently === true) && !editingLeadData.current_hrms_tool && (
                    <div className="space-y-1">
                      <Label className="text-amber-800 text-xs">Current HRMS Tool *</Label>
                      <Select
                        value={editingLeadData.current_hrms_tool || ''}
                        onValueChange={(v) => setEditingLeadData({...editingLeadData, current_hrms_tool: v})}
                      >
                        <SelectTrigger className="border-amber-300">
                          <SelectValue placeholder="Select tool" />
                        </SelectTrigger>
                        <SelectContent>
                          {hrmsTools.map(tool => (
                            <SelectItem key={tool} value={tool}>{tool}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Demo Scheduling Form */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-700">Demo Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Demo Date *</Label>
                  <Input
                    type="date"
                    value={demoForm.demo_date}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setDemoForm({...demoForm, demo_date: e.target.value})}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Start Time *</Label>
                  <Input
                    type="time"
                    value={demoForm.demo_time}
                    onChange={(e) => setDemoForm({...demoForm, demo_time: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Duration</Label>
                  <Select value={demoForm.duration} onValueChange={(v) => setDemoForm({...demoForm, duration: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">60 minutes</SelectItem>
                      <SelectItem value="90">90 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Meeting Link</Label>
                  <Input
                    value={demoForm.meeting_link}
                    onChange={(e) => setDemoForm({...demoForm, meeting_link: e.target.value})}
                    placeholder="https://meet.google.com/..."
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Notes</Label>
                <Textarea
                  value={demoForm.notes}
                  onChange={(e) => setDemoForm({...demoForm, notes: e.target.value})}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDemoOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleScheduleDemo}
              disabled={!demoForm.demo_date || !demoForm.demo_time || (demoMissingFields.length > 0 && (
                (demoMissingFields.includes('Email') && !editingLeadData.contact_email) ||
                (demoMissingFields.includes('City') && !editingLeadData.city) ||
                (demoMissingFields.includes('Employee Count') && !editingLeadData.total_employee_count) ||
                (demoMissingFields.includes('Industry') && !editingLeadData.industry) ||
                (demoMissingFields.includes('Using HRMS Currently') && editingLeadData.using_hrms_currently === undefined)
              ))}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <CalendarPlus className="w-4 h-4 mr-2" />
              Schedule Demo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Lead Modal */}
      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Ban className="w-5 h-5" />
              Reject Lead
            </DialogTitle>
            <DialogDescription>
              This will move the lead to Lead Bank (Admin).
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Reason for Rejection *</Label>
            <Textarea 
              value={rejectReason} 
              onChange={(e) => setRejectReason(e.target.value)} 
              placeholder="Enter reason..."
              rows={3}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectModalOpen(false); setRejectReason(''); }}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleRejectLead}
              disabled={!rejectReason.trim()}
            >
              Reject Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={dialogProps.open}
        onOpenChange={dialogProps.onOpenChange}
        title={dialogProps.title}
        description={dialogProps.description}
        confirmText={dialogProps.confirmText}
        cancelText={dialogProps.cancelText}
        destructive={dialogProps.destructive}
        onConfirm={dialogProps.onConfirm}
        onCancel={dialogProps.onCancel}
      />
    </div>
  );
};

export default LeadsPage;